import React, { useEffect, useState } from "react";
import {
  Row, Col, Button, notification, Form, Select, InputNumber,
  Descriptions, Alert, Divider
} from "antd";
import axios from "axios";
import { SaveOutlined, BookOutlined } from '@ant-design/icons';
import { baseUrl } from "../../config";

const { Option } = Select;

const sectionLabel = (title) => (
  <Divider orientation="left" style={{ color: '#7a0000', fontWeight: 700, fontSize: 13, borderColor: '#d0dce8', margin: '16px 0 8px' }}>
    {title}
  </Divider>
);

function BudgetAllocationsForm({ 
  budgetId, 
  onRefreshData = () => {}, 
  purposesList = [] 
}) {
  const [form] = Form.useForm();
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedPurpose, setSelectedPurpose] = useState(null);
  const [existingSpent, setExistingSpent] = useState(0);

  useEffect(() => {
    if (budgetId) {
      setIsUpdateMode(true);
      fetchBudgetDetails();
    }
  }, [budgetId]);

  const fetchBudgetDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseUrl}/budget-allocations/read/${budgetId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      
      if (response.status === 200) {
        const data = response.data.result;
        form.setFieldsValue(data);
        setExistingSpent(parseFloat(data.spent_amount) || 0);
        
        // Find and set selected purpose
        const purpose = purposesList.find(p => p.id === data.purpose_id);
        setSelectedPurpose(purpose);
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to fetch budget allocation details',
        placement: 'topRight'
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePurposeChange = (purposeId) => {
    const purpose = purposesList.find(p => p.id === purposeId);
    setSelectedPurpose(purpose);
  };

  const onFinish = async (values) => {
    try {
      setLoading(true);

      let response;
      if (isUpdateMode) {
        response = await axios.patch(`${baseUrl}/budget-allocations/update/${budgetId}`, values, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        });
      } else {
        response = await axios.post(`${baseUrl}/budget-allocations/create`, values, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        });
      }

      if (response.status === 200) {
        notification.success({
          message: 'Success',
          description: isUpdateMode ? 'Budget allocation updated successfully!' : 'Budget allocation created successfully!',
          placement: 'topRight'
        });
        
        onRefreshData();
        
        if (!isUpdateMode) {
          form.resetFields();
          setSelectedPurpose(null);
          setExistingSpent(0);
        }
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: error.response?.data?.message || 'Operation failed',
        placement: 'topRight'
      });
    } finally {
      setLoading(false);
    }
  };

  const getCurrentFiscalYear = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    
    if (currentMonth >= 3) { // April onwards
      return `${currentYear}-${(currentYear + 1).toString().slice(-2)}`;
    } else {
      return `${currentYear - 1}-${currentYear.toString().slice(-2)}`;
    }
  };

  const getFiscalYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const options = [];
    
    for (let i = -1; i <= 3; i++) {
      const year = currentYear + i;
      const fiscalYear = `${year}-${(year + 1).toString().slice(-2)}`;
      options.push(
        <Option key={fiscalYear} value={fiscalYear}>
          FY {fiscalYear}
        </Option>
      );
    }
    
    return options;
  };

  const validateAllocatedAmount = (_, value) => {
    if (isUpdateMode && value < existingSpent) {
      return Promise.reject(new Error(`Allocated amount cannot be less than already spent amount (₹${existingSpent.toLocaleString('en-IN')})`));
    }
    return Promise.resolve();
  };

  return (
    <div style={{ padding: '4px 0 24px' }}>
      <Form form={form} onFinish={onFinish} layout="vertical" requiredMark={false}
        initialValues={{ fiscal_year: getCurrentFiscalYear(), spent_amount: 0 }}>

        {isUpdateMode && (
          <Alert message={`Current spent amount: ₹${existingSpent.toLocaleString('en-IN')}. Allocated amount cannot be less than spent.`}
            type="info" showIcon style={{ marginBottom: 16 }} />
        )}

        {sectionLabel('Allocation Details')}
        <Row gutter={[12, 0]}>
          <Col xs={24} sm={12}>
            <Form.Item label="Fiscal Year" name="fiscal_year" rules={[{ required: true, message: 'Required' }]}>
              <Select placeholder="Select fiscal year" disabled={isUpdateMode}>{getFiscalYearOptions()}</Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="Purpose" name="purpose_id" rules={[{ required: true, message: 'Required' }]}>
              <Select placeholder="Select purpose" showSearch optionFilterProp="children"
                onChange={handlePurposeChange} disabled={isUpdateMode}
                filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}>
                {purposesList.map(p => (
                  <Option key={p.id} value={p.id}><BookOutlined style={{ marginRight: 6 }} />{p.name} ({p.code})</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="Allocated Amount" name="allocated_amount"
              rules={[{ required: true, message: 'Required' }, { type: 'number', min: 0.01 }, { validator: validateAllocatedAmount }]}>
              <InputNumber style={{ width: '100%' }} min={0} placeholder="Allocated amount"
                formatter={v => `₹ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={v => v.replace(/₹\s?|(,*)/g, '')} />
            </Form.Item>
          </Col>
          {isUpdateMode && (
            <Col xs={24} sm={12}>
              <Form.Item label="Spent Amount" name="spent_amount" rules={[{ type: 'number', min: 0 }]}>
                <InputNumber style={{ width: '100%' }} min={0} placeholder="Spent amount"
                  formatter={v => `₹ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={v => v.replace(/₹\s?|(,*)/g, '')} />
              </Form.Item>
            </Col>
          )}
        </Row>

        {selectedPurpose && (
          <>
            {sectionLabel('Purpose Details')}
            <Descriptions column={2} size="small" style={{ marginBottom: 8 }}>
              <Descriptions.Item label="Name">{selectedPurpose.name}</Descriptions.Item>
              <Descriptions.Item label="Code">{selectedPurpose.code}</Descriptions.Item>
              <Descriptions.Item label="Category">{selectedPurpose.category?.replace('_', ' ')}</Descriptions.Item>
              <Descriptions.Item label="Max Limit">
                {selectedPurpose.max_amount_limit ? `₹${Number(selectedPurpose.max_amount_limit).toLocaleString('en-IN')}` : 'No Limit'}
              </Descriptions.Item>
            </Descriptions>
          </>
        )}

        <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
          <Button htmlType="submit" loading={loading} icon={<SaveOutlined />} size="large"
            style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff', minWidth: 180 }}>
            {isUpdateMode ? 'Update Budget Allocation' : 'Save Budget Allocation'}
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default BudgetAllocationsForm;